import * as ImagePicker from 'expo-image-picker';
import { Box, Flex, View, useBreakpointValue } from 'native-base';
import React, { useEffect, useState, useRef } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { v4 as uuid } from 'uuid';

import { OvalButton, Button } from '~/components/button';
import { CenteredEllipticMask } from '~/components/elliptic-mask/centered-elliptic-mask';
import { ButtonPlusIcon } from '~/components/icon';
import { Image } from '~/components/image';
import { MaskedView } from '~/components/masked-view';
import { useIntl } from '~/contexts/intl';
import { AppError } from '~/error';
import { isWeb } from '~/utils';

import { Color } from '../color';
import { EllipticMaskView } from '../elliptic-mask';
import { Spacing } from '../spacing';

export enum ProfileImageSize {
    REGULAR = 232,
    SMALL = 148
}

type ProfileImageProps = {
    editable?: boolean;
    imageUrl?: string;
    size?: ProfileImageSize;
    // The optional file parameter carries the native File object from expo-image-picker
    // on web. This allows the upload to use the File directly instead of fetching the URI,
    // which would be blocked by the deployed app's Content Security Policy (connect-src).
    onImageSelected?: (uri: string, file?: File) => Promise<void>;
};

export const ProfileImage: React.FC<ProfileImageProps> = props => {
    const { formatMessage } = useIntl();

    const { size = ProfileImageSize.REGULAR } = props;
    const { editable, imageUrl, onImageSelected } = props;
    const [selectedImageUri, setSelectedImageUri] = useState<string | undefined>(undefined);
    const mounted = useRef(false);
    const handleError = useErrorHandler();

    // We need to track component's mounted state in order to prevent updating `selectedImageUri` if the user
    // navigates away from the view while the `onImageSelected` async call is in progress.

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const handleRequestPermission = async () => {
        let { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();

        if (status === 'undetermined') {
            const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
            status = permission.status;
        }
        return status === 'granted';
    };

    const pickImage = async () => {
        try {
            if (onImageSelected) {
                if (await handleRequestPermission()) {
                    const result = await ImagePicker.launchImageLibraryAsync({
                        mediaTypes: ImagePicker.MediaTypeOptions.Images,
                        allowsEditing: true,
                        aspect: [1, 1],
                        quality: 1,
                        exif: false
                    });
                    if (!result.canceled) {
                        const asset = result.assets[0];
                        // Pass the native File object (available on web via expo-image-picker)
                        // so the upload handler can use it directly instead of fetching the URI
                        await onImageSelected(asset.uri, (asset as { file?: File }).file);
                        if (mounted.current) {
                            setSelectedImageUri(result.assets[0].uri);
                        }
                    }
                }
            }
        } catch (error: unknown) {
            if (error instanceof Error) {
                handleError(
                    new AppError(error, 'profile.error.image-upload-failed', {
                        onClose: () => {},
                        name: 'error-overlay.go_back'
                    })
                );
            } else {
                handleError(error);
            }
        }
    };

    const maskId = uuid();

    return (
        <Flex direction="column" alignItems="center">
            <View accessibilityRole="image" accessibilityLabel={formatMessage('accessibility.profile-picture')}>
                <MaskedView
                    maskId={maskId}
                    style={{ width: size, height: size }}
                    maskElement={<CenteredEllipticMask maskId={maskId} />}
                >
                    <Image
                        source={{ uri: selectedImageUri ?? imageUrl }}
                        contentFit="cover"
                        style={[{ width: size, aspectRatio: 1 }]}
                    />
                </MaskedView>
                <Box variant="flexMobileOnly" position="absolute" bottom="8px" right="8px">
                    {editable && onImageSelected ? (
                        <OvalButton
                            size="large"
                            icon={ButtonPlusIcon}
                            onPress={pickImage}
                            accessibilityLabel={formatMessage('accessibility.change-profile-picture')}
                        />
                    ) : null}
                </Box>
            </View>

            <Box variant="flexDesktopOnly">
                {editable && onImageSelected ? (
                    <Button
                        shape="rounded"
                        size="small"
                        label={formatMessage('profile.image.edit')}
                        onPress={pickImage}
                        style={{ marginTop: Spacing.MEDIUM, alignSelf: 'flex-start' }}
                    />
                ) : null}
            </Box>
        </Flex>
    );
};

export const ProfileCardImage: React.FC<ProfileImageProps> = props => {
    const { imageUrl, onImageSelected, editable, size } = props;

    const showDesktopComponent = useBreakpointValue({
        base: false,
        md: true
    });

    const IMAGE_HEIGHT = isWeb() ? 232 : 350;

    return (
        <>
            {showDesktopComponent ? (
                <ProfileImage
                    editable={editable}
                    imageUrl={imageUrl ?? undefined}
                    onImageSelected={onImageSelected}
                    size={size}
                />
            ) : (
                <EllipticMaskView ellipseSize="large" style={{ height: IMAGE_HEIGHT }}>
                    <Flex
                        flexDirection="column"
                        flexBasis="350"
                        width="100%"
                        flexGrow={1}
                        alignItems="center"
                        justifyContent="center"
                        backgroundColor={Color.BACKGROUND_PRIMARY}
                        height={IMAGE_HEIGHT}
                    >
                        <ProfileImage
                            editable={editable}
                            imageUrl={imageUrl ?? undefined}
                            onImageSelected={onImageSelected}
                            size={size}
                        />
                    </Flex>
                </EllipticMaskView>
            )}
        </>
    );
};

ProfileImage.displayName = 'ProfileImage';
